import { useEffect, useMemo, useRef, useState } from "react";
import TagManager from "react-gtm-module";
import Link from "next/link";
import { Interpolation, Theme, css, useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import { OriginalContentCard } from "components/cards/OriginalContentCard";
import { OriginalUpcomingCard } from "components/cards/OriginalUpcomingCard";
import { SectionScaffold } from "components/scaffolds/SectionScaffold";
import { Chevron } from "components/svgs/Chevron";
import { MoveIcon } from "components/svgs/MoveIcon";
import { useMixContentQuery } from "graphql/generated";
import { useAuth } from "hooks/useAuth";
import _ from "lodash";
import { slidein } from "styles/animation/slidein";

const SeeMore = styled(Link)`
  display: flex;
  align-items: center;
  column-gap: 10px;
`;

const MoveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 60px;
  height: 60px;
  border-radius: 100%;
  background-color: ${({ theme }) => theme.color.grey_12};
  color: ${({ theme }) => theme.color.grey_f};
  z-index: 1;

  &:disabled {
    display: none;
  }

  @media (max-width: 1024px) {
    display: none;
  }
`;

export const OriginalSection = ({
  contentSection,
  variables,
  limit = 8,
  ...props
}: {
  contentSection: ContentSection;
  variables: MixContentQueryVariables;
  limit?: number;
} & { css?: Interpolation<Theme> }) => {
  const theme = useTheme();
  // TODO: PG사 심사 후 제거
  const { me } = useAuth();

  const ref = useRef<HTMLDivElement>(null);
  const [showPrevBtn, setShowPrevBtn] = useState(false);
  const [showNextBtn, setShowNextBtn] = useState(false);

  const { data } = useMixContentQuery({
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
    variables: {
      ...variables,
      limit,
      offset: 0,
    },
  });

  const contents = useMemo(() => (data ? data.mix_content : []), [data]);

  const goPrev = () => {
    if (!ref.current) return;

    ref.current.scrollBy({
      left: -ref.current.clientWidth,
      behavior: "smooth",
    });
  };

  const goNext = () => {
    if (!ref.current) return;

    ref.current.scrollBy({
      left: ref.current.clientWidth,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    // cleanup은 화면 업데이트 이후 실행 되므로 ref.current 값이 변경될 가능성이 있어 복사본을 생성해둔다.
    if (!ref.current) return;
    const refInstance = ref.current;

    const callback = _.throttle(() => {
      const { clientWidth, scrollWidth, scrollLeft } = refInstance;

      const errorMargin = 10;
      const isOverflow = clientWidth < scrollWidth;
      const isAtEnd = scrollLeft + clientWidth + errorMargin >= scrollWidth;

      setShowPrevBtn(isOverflow && scrollLeft > 0);
      setShowNextBtn(isOverflow && !isAtEnd);
    }, 300);

    callback();

    window.addEventListener("resize", callback);
    refInstance.addEventListener("scroll", callback);

    return () => {
      window.removeEventListener("resize", callback);
      refInstance.removeEventListener("scroll", callback);
    };
  }, [ref]);

  return (
    <SectionScaffold {...props}>
      <SectionScaffold.Header>
        <SectionScaffold.Title>오리지널 콘텐츠</SectionScaffold.Title>
        <SectionScaffold.Controls>
          <SeeMore href="/original">
            <span
              css={css`
                font-size: 14px;
                font-weight: 700;
                line-height: 1;
                color: ${theme.color.grey_9};

                @media (max-width: 768px) {
                  font-weight: 400;
                }
              `}
            >
              더보기
            </span>
            <Chevron
              css={css`
                color: ${theme.color.grey_9};
              `}
            />
          </SeeMore>
        </SectionScaffold.Controls>
      </SectionScaffold.Header>
      <SectionScaffold.Contents>
        <div
          css={css`
            position: relative;
          `}
        >
          <MoveButton
            type="button"
            disabled={!showPrevBtn}
            onClick={goPrev}
            css={css`
              left: -30px;
            `}
          >
            <MoveIcon
              css={css`
                transform: rotate(180deg);
              `}
            />
          </MoveButton>
          <div
            ref={ref}
            css={css`
              display: grid;
              grid-auto-flow: column;
              grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
              grid-column-gap: 16px;
              overflow: auto;
              scroll-snap-type: x mandatory;
              scroll-padding: var(--page-padding);
              animation: 600ms ${slidein};

              ::-webkit-scrollbar {
                display: none;
              }

              @media (max-width: 1024px) {
                display: flex;
                column-gap: 16px;
                max-width: 100vw;
                padding: 0 var(--page-padding);
                margin: 0 calc(-1 * var(--page-padding));
              }

              @media (max-width: 768px) {
                column-gap: 12px;
              }
            `}
          >
            {contents.map((content) => (
              <div
                key={content.id}
                css={css`
                  // TODO: PG사 심사 후 제거
                  ${me?.email !== process.env.NEXT_PUBLIC_TEST_EMAIL &&
                  content.id === "86d53da5-4f92-4ac2-82cf-8d2c93a0439e" &&
                  css`
                    display: none;
                  `}

                  min-width: 300px;
                  scroll-snap-align: start;

                  @media (max-width: 1024px) {
                    min-width: 280px;
                  }

                  @media (max-width: 768px) {
                    min-width: 235px;
                  }
                `}
                onClick={() => {
                  const params = {
                    content_section: contentSection,
                    content_url: content.originalurl,
                    content_isoriginal: true,
                    content_released: content.released,
                  };

                  TagManager.dataLayer({
                    dataLayer: {
                      event: "content_click",
                      ...params,
                      content_id: content.id,
                      content_title: content.title,
                    },
                  });

                  window.fbq("track", "ViewContent", {
                    ...params,
                    content_ids: [content.id],
                    content_name: content.title,
                    content_type: "product",
                  });
                }}
              >
                {content.released ? (
                  <OriginalContentCard content={content} />
                ) : (
                  <OriginalUpcomingCard content={content} />
                )}
              </div>
            ))}
          </div>
          <MoveButton
            type="button"
            disabled={!showNextBtn}
            onClick={goNext}
            css={css`
              right: -30px;
            `}
          >
            <MoveIcon />
          </MoveButton>
        </div>
      </SectionScaffold.Contents>
    </SectionScaffold>
  );
};
